import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useApolloClient } from '@apollo/client';

import { Spinner } from 'components/Shared';
import { Education } from '../../Sections/Education';
import { IdentificationABN, IdentificationNPI } from '../../Sections/Identification';
import { LiabilityInsurance, LiabilityInsuranceAlert } from '../../Sections/LiabilityInsurance';
import { ProfessionalDesignation } from '../../Sections/ProfessionalDesignation';
import { LicensesAndRegistrations } from '../../Sections/LicensesAndRegistrations';
import { PreviousExperience } from '../../Sections/PreviousExperience';
import { ProfessionalDevelopment } from '../../Sections/ProfessionalDevelopment';
import { Specializations } from '../../Sections/Specializations';
import { type LiabilityInsurance as ILiabilityInsurance, type License, type RenewLicenseHistoryState } from '../../../interfaces';
import { CREDENTIALING_QUERY } from './queries';
import { LicensesAndRegistrationsAlert } from '../../Sections/LicensesAndRegistrations/LicensesAndRegistrationsAlert';
import { useHistoryState } from 'hooks/useHistoryState';
import { useSearchFiles } from 'api/queries/fileUploadQuery';

import './Credentialing.scss';

interface CredentialingProps {
    providerId: number
}

export const Credentialing: FC<CredentialingProps> = ({ providerId }) => {
    const { ready: translationsReady } = useTranslation('providerprofile', { useSuspense: false });

    const { cache } = useApolloClient();

    const { data: allAttachments } = useSearchFiles({ Type: 'provider', Id: providerId });

    const [, setHistoryState] = useHistoryState<RenewLicenseHistoryState>();

    const onRenewClick = (renewLicenseId: number) => setHistoryState({ renewLicenseId });

    const attachFilesToLicense = (license?: License) => {
        return { 
            ...license,
            attachments: allAttachments?.filter(attachment => (
                attachment?.metadata.providerLicenseId === license?.providerLicenseId.toString()
                && attachment?.metadata.document_type === 'professional-membership-license'
            )) 
        } as License;
    }

    const credentialingQuery = useQuery(CREDENTIALING_QUERY, {
        fetchPolicy: 'network-only',
        variables: { providerId },
        errorPolicy: 'all'
    });

    useEffect(() => {
        if (!!allAttachments?.length && credentialingQuery.called && !!credentialingQuery.data) {
            handleLicensesUpdated(cache.readQuery({ query: CREDENTIALING_QUERY, variables: { providerId }})?.provider.credential.licenses?.map(attachFilesToLicense));
        }
    }, [allAttachments, credentialingQuery?.data]);

    const handleEducationsUpdated = (educations) => {
        cache.writeQuery({
            query: CREDENTIALING_QUERY,
            variables: { providerId: providerId },
            data: {
                ...credentialingQuery.data,
                provider: {
                    ...credentialingQuery.data?.provider,
                    credential: {
                        ...credentialingQuery.data?.provider.credential,
                        educations: educations
                    }
            }}
        })
    }
    
    const handleLicensesUpdated = (licenses) => {
        cache.writeQuery({
            query: CREDENTIALING_QUERY,
            variables: { providerId: providerId },
            data: {
                ...credentialingQuery.data,
                provider: {
                    ...credentialingQuery.data?.provider,
                    credential: {
                        ...credentialingQuery.data?.provider.credential,
                        licenses: licenses
                    }
            }}
        })
    }
    
    // const handleABNUpdated = (abn) => {
    //     cache.writeQuery({
    //         query: CREDENTIALING_QUERY,
    //         variables: { providerId: providerId },
    //         data: {
    //             ...credentialingQuery.data,
    //             provider: {
    //                 ...credentialingQuery.data?.provider,
    //                 credential: {
    //                     ...credentialingQuery.data?.provider.credential,
    //                     businessNumber: abn
    //                 }
    //         }}
    //     })
    // }

    const handleNPIUpdated = (npi) => {
        cache.writeQuery({
            query: CREDENTIALING_QUERY,
            variables: { providerId: providerId },
            data: {
                ...credentialingQuery.data,
                provider: {
                    ...credentialingQuery.data?.provider,
                    credential: {
                        ...credentialingQuery.data?.provider.credential,
                        nationalProviderIdentifier: npi
                    }
            }}
        })
    }

    const handlePreviousExperienceUpdated = (previousExperience) => {
        cache.writeQuery({
            query: CREDENTIALING_QUERY,
            variables: { providerId: providerId },
            data: {
                ...credentialingQuery.data,
                provider: {
                    ...credentialingQuery.data?.provider,
                    credential: {
                        ...credentialingQuery.data?.provider.credential,
                        previousExperience: previousExperience
                    }
            }}
        })
    }
    
    const handleProfessionalDevelopmentUpdated = (professionalDevelopment) => {
        cache.writeQuery({
            query: CREDENTIALING_QUERY,
            variables: { providerId: providerId },
            data: {
                ...credentialingQuery.data,
                provider: {
                    ...credentialingQuery.data?.provider,
                    credential: {
                        ...credentialingQuery.data?.provider.credential,
                        professionalDevelopment: professionalDevelopment
                    }
            }}
        })
    }
    
    const handleProfessionalDesignationUpdated = (professionalDesignation) => {
        cache.writeQuery({
            query: CREDENTIALING_QUERY,
            variables: { providerId: providerId },
            data: {
                ...credentialingQuery.data,
                provider: {
                    ...credentialingQuery.data?.provider,
                    credential: {
                        ...credentialingQuery.data?.provider.credential,
                        professionalDesignation: professionalDesignation
                    }
            }}
        })
    }

    const handleSpecializationsUpdated = (specializations) => {
        cache.writeQuery({
            query: CREDENTIALING_QUERY,
            variables: { providerId: providerId },
            data: {
                ...credentialingQuery.data,
                provider: {
                    ...credentialingQuery.data?.provider,
                    credential: {
                        ...credentialingQuery.data?.provider.credential,
                        specializations: specializations
                    }
            }}
        })
    }

    const handleLiabilityInsuranceUpdated = (liabilityInsurance: ILiabilityInsurance) => {
        cache.writeQuery({
            query: CREDENTIALING_QUERY,
            variables: { providerId: providerId },
            data: {
                ...credentialingQuery.data,
                provider: {
                    ...(credentialingQuery.data?.provider as any),
                    liabilityInsurance: liabilityInsurance
            }}
        })
    }


    const loading = !translationsReady || credentialingQuery.loading;

    const showNPI = credentialingQuery.data?.provider?.credential?.showNationalProviderIdentifier ?? false;
    const showABN = credentialingQuery.data?.provider?.credential?.showBusinessNumber ?? false;

    const educations = credentialingQuery.data?.provider?.credential?.educations;
    const licenses = credentialingQuery.data?.provider?.credential?.licenses?.map(attachFilesToLicense);
    const previousExperience = credentialingQuery.data?.provider?.credential?.previousExperience;
    const professionalDevelopment = credentialingQuery.data?.provider?.credential?.professionalDevelopment;
    const professionalDesignation = credentialingQuery.data?.provider?.credential?.professionalDesignation;
    const nationalProviderIdentifier = credentialingQuery.data?.provider?.credential?.nationalProviderIdentifier ?? undefined;
    const businessNumber = credentialingQuery.data?.provider?.credential?.businessNumber ?? undefined;
    const specializations = credentialingQuery.data?.provider?.credential?.specializations;

    return loading
        ? <Spinner fillParent/>
        : <>
            {
                showABN &&
                <IdentificationABN ABN={businessNumber} />
            }
            {
                showNPI &&
                <IdentificationNPI providerId={providerId} NPI={nationalProviderIdentifier} onNPIUpdated={handleNPIUpdated}/>
            }
            <LiabilityInsuranceAlert />
            <LicensesAndRegistrationsAlert type="expired" onRenewClick={onRenewClick} />
            <LicensesAndRegistrationsAlert type="expiring" onRenewClick={onRenewClick} />
            <LiabilityInsurance providerId={providerId} onUpdated={handleLiabilityInsuranceUpdated}/>
            <Education providerId={providerId} educations={educations} onEducationsUpdated={handleEducationsUpdated}/>
            <Specializations providerId={providerId} specializations={specializations} onSpecializationsUpdated={handleSpecializationsUpdated}/>
            <LicensesAndRegistrations providerId={providerId} licenses={licenses} onLicensesUpdated={handleLicensesUpdated}/>
            <ProfessionalDesignation providerId={providerId} professionalDesignations={professionalDesignation} onProfessionalDesignationsUpdated={handleProfessionalDesignationUpdated}/>
            <PreviousExperience providerId={providerId} previousExperience={previousExperience} onPreviousExperienceUpdated={handlePreviousExperienceUpdated}/>
            <ProfessionalDevelopment providerId={providerId} professionalDevelopment={professionalDevelopment} onProfessionalDevelopmentUpdated={handleProfessionalDevelopmentUpdated}/>
        </>
}
